import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate } from "react-router-dom";
import { apiUrl } from "./api";

const introMessage = { from: "bot", text: "Hello! I'm ready to help you find relevant cases under Council of Europe conventions for international law." };

function Home({ onLogout, userEmail, authEnabled, token }) {
  const [messages, setMessages] = useState([introMessage]);
  const [botTyping, setBotTyping] = useState(false); // kept for typing animation
  const [inputValue, setInputValue] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    if (!authEnabled || !token) return;
    let active = true;
    fetch(apiUrl("/api/chat-history"), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error("History unavailable")))
      .then((data) => {
        if (active && Array.isArray(data.messages) && data.messages.length) {
          setMessages([introMessage, ...data.messages]);
        }
      })
      .catch((err) => console.warn(err));
    return () => { active = false; };
  }, [authEnabled, token]);




  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages or typing change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, botTyping]);


async function handleSubmit(e) {
  e.preventDefault();
  const trimmed = inputValue.trim();
  if (!trimmed) return;

  // Add user message and build history including it
  const newMessages = [
    ...messages,
    { from: "user", text: trimmed },
  ];
  setMessages(newMessages);
  setInputValue("");

  try {
  setBotTyping(true);

  const chatHistory = messages.filter((m) => m !== messages[0]).slice(-12).map((m) => ({
    role: m.from === "bot" ? "assistant" : "user",
    content: m.text.slice(0, 4000),
  }));

  const response = await fetch(apiUrl("/chat"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      query: trimmed,
      history: chatHistory,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    if (response.status === 401 && authEnabled) onLogout();
    throw new Error(data.detail || `Backend returned ${response.status}`);
  }
  setBotTyping(false);

  // ✅ Just show whatever the backend sends (analysis + links)
  const completedMessages = [...newMessages, { from: "bot", text: data.answer || "No answer from backend." }];
  setMessages(completedMessages);
  if (authEnabled && token) {
    fetch(apiUrl("/api/chat-history"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ messages: completedMessages.slice(1).slice(-50) }),
    }).catch((err) => console.warn("Could not save chat history", err));
  }
} catch (err) {
  console.error(err);
  setBotTyping(false);
  setMessages((prev) => [
    ...prev,
    { from: "bot", text: err.message || "Error talking to backend." },
  ]);
}

}




return (
  <>
    <div
      className={
        "flex h-screen relative " +
        (isDarkMode ? "bg-slate-900 text-gray-100" : "bg-gray-50 text-gray-900")
      }
    >

        {/* Backdrop when history is open on small screens */}
        {isHistoryOpen && (
          <div
            className="fixed inset-0 bg-slate-900/30 z-20"
            onClick={() => setIsHistoryOpen(false)}
          />
        )}

        {/* Chat History container */}
        <div
          className={`
            border flex flex-col justify-between p-2 sm:p-6
            transition-transform duration-200 ease-in-out
            w-64 min-w-44
            fixed inset-y-0 left-0 z-30
            ${isHistoryOpen ? "translate-x-0 w-2/12" : "-translate-x-full"}
            ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-gray-100 border-gray-200"}
            
          `}
        >
          {/* Header with close button on small screens */}
          <div className="flex items-center justify-between mb-2">
            <h2
  className={
    "font-semibold text-sm sm:text-base " +
    (isDarkMode ? "text-gray-100" : "text-gray-900")
  }
>

              Chat History
            </h2>
            <button
              className={
    "text-xs px-1 py-1 rounded-full " +
    (isDarkMode ? "text-gray-100 hover:bg-slate-700" : "text-black hover:bg-gray-200")
  }
              onClick={() => setIsHistoryOpen(false)}
            >
              <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    className="w-5 h-5"
    fill="currentColor"
  >
    <path d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z"/>
  </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto text-sm">
            {messages.filter((m) => m.from === "user").length === 0 ? (
              <p className="text-gray-500">Your questions will appear here.</p>
            ) : messages.map((message, index) => message.from === "user" && (
              <button key={index} type="button" className="block w-full text-left truncate rounded p-2 hover:bg-slate-700" onClick={() => {
                setIsHistoryOpen(false);
                document.getElementById(`message-${index}`)?.scrollIntoView({ behavior: "smooth" });
              }}>{message.text}</button>
            ))}
          </div>

          {/* Optional footer in history */}
          <div className="relative flex inline-block" ref={ref}>
      {/* Profile Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={"w-full h-10 flex items-center rounded-md justify-center font-semibold transition" + (isDarkMode ? "bg-black text-white hover:bg-slate-600" : "text-gray-100 hover:bg-slate-200")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 640 640"
            className="w-5 h-5"
            fill="gray">
        <path d="M240 192C240 147.8 275.8 112 320 112C364.2 112 400 147.8 400 192C400 236.2 364.2 272 320 272C275.8 272 240 236.2 240 192zM448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320C390.7 320 448 262.7 448 192zM144 544C144 473.3 201.3 416 272 416L368 416C438.7 416 496 473.3 496 544L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 544C544 446.8 465.2 368 368 368L272 368C174.8 368 96 446.8 96 544L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 544z"/>
        </svg>
        <p className="pl-1 font-light text-xs truncate" title={userEmail}>{userEmail || "Guest"}</p>
      </button>
      
      {/* Drop-Up Menu */}
      {open && (
        <div className={"absolute bottom-12 right-0  shadow-xl rounded-lg py-2 w-40 z-50 animate-fadeIn" + (isDarkMode ? "text-gray-100 bg-black" : "text-black bg-white")}>
          <button
            onClick={() => navigate("/settings")}
            className={"w-full flex text-left px-4 py-2 text-sm transition"+ (isDarkMode ? "bg-black hover:bg-gray-600" : "bg-white hover:bg-gray-200")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 640 640"
            className="w-5 h-5 mr-1"
            fill="currentColor"> 
            <path d="M259.1 73.5C262.1 58.7 275.2 48 290.4 48L350.2 48C365.4 48 378.5 58.7 381.5 73.5L396 143.5C410.1 149.5 423.3 157.2 435.3 166.3L503.1 143.8C517.5 139 533.3 145 540.9 158.2L570.8 210C578.4 223.2 575.7 239.8 564.3 249.9L511 297.3C511.9 304.7 512.3 312.3 512.3 320C512.3 327.7 511.8 335.3 511 342.7L564.4 390.2C575.8 400.3 578.4 417 570.9 430.1L541 481.9C533.4 495 517.6 501.1 503.2 496.3L435.4 473.8C423.3 482.9 410.1 490.5 396.1 496.6L381.7 566.5C378.6 581.4 365.5 592 350.4 592L290.6 592C275.4 592 262.3 581.3 259.3 566.5L244.9 496.6C230.8 490.6 217.7 482.9 205.6 473.8L137.5 496.3C123.1 501.1 107.3 495.1 99.7 481.9L69.8 430.1C62.2 416.9 64.9 400.3 76.3 390.2L129.7 342.7C128.8 335.3 128.4 327.7 128.4 320C128.4 312.3 128.9 304.7 129.7 297.3L76.3 249.8C64.9 239.7 62.3 223 69.8 209.9L99.7 158.1C107.3 144.9 123.1 138.9 137.5 143.7L205.3 166.2C217.4 157.1 230.6 149.5 244.6 143.4L259.1 73.5zM320.3 400C364.5 399.8 400.2 363.9 400 319.7C399.8 275.5 363.9 239.8 319.7 240C275.5 240.2 239.8 276.1 240 320.3C240.2 364.5 276.1 400.2 320.3 400z"/>
            </svg>
             Settings
          </button>

          <button
            onClick={() => navigate("/contact")}
            className={"w-full flex text-left px-4 py-2 text-sm transition"+ (isDarkMode ? "bg-black hover:bg-gray-600" : "bg-white hover:bg-gray-200")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 640 640"
            className="w-5 h-5 mr-1"
            fill="currentColor">
            <path d="M64 304C64 358.4 83.3 408.6 115.9 448.9L67.1 538.3C65.1 542 64 546.2 64 550.5C64 564.6 75.4 576 89.5 576C93.5 576 97.3 575.4 101 573.9L217.4 524C248.8 536.9 283.5 544 320 544C461.4 544 576 436.5 576 304C576 171.5 461.4 64 320 64C178.6 64 64 171.5 64 304zM158 471.9C167.3 454.8 165.4 433.8 153.2 418.7C127.1 386.4 112 346.8 112 304C112 200.8 202.2 112 320 112C437.8 112 528 200.8 528 304C528 407.2 437.8 496 320 496C289.8 496 261.3 490.1 235.7 479.6C223.8 474.7 210.4 474.8 198.6 479.9L140 504.9L158 471.9zM208 336C225.7 336 240 321.7 240 304C240 286.3 225.7 272 208 272C190.3 272 176 286.3 176 304C176 321.7 190.3 336 208 336zM352 304C352 286.3 337.7 272 320 272C302.3 272 288 286.3 288 304C288 321.7 302.3 336 320 336C337.7 336 352 321.7 352 304zM432 336C449.7 336 464 321.7 464 304C464 286.3 449.7 272 432 272C414.3 272 400 286.3 400 304C400 321.7 414.3 336 432 336z"/>
            </svg>
             Contact us
          </button>

          {authEnabled && <button
            onClick={onLogout}
            className={"w-full flex text-left px-4 py-2 text-sm transition "+ (isDarkMode ? "text-red-600 bg-black hover:bg-gray-600" : "text-red-600 hover:bg-gray-300")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 640 640"
            className="w-5 h-5 mr-1"
            fill="currentColor">
            <path d="M224 160C241.7 160 256 145.7 256 128C256 110.3 241.7 96 224 96L160 96C107 96 64 139 64 192L64 448C64 501 107 544 160 544L224 544C241.7 544 256 529.7 256 512C256 494.3 241.7 480 224 480L160 480C142.3 480 128 465.7 128 448L128 192C128 174.3 142.3 160 160 160L224 160zM566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L438.6 169.3C426.1 156.8 405.8 156.8 393.3 169.3C380.8 181.8 380.8 202.1 393.3 214.6L466.7 288L256 288C238.3 288 224 302.3 224 320C224 337.7 238.3 352 256 352L466.7 352L393.3 425.4C380.8 437.9 380.8 458.2 393.3 470.7C405.8 483.2 426.1 483.2 438.6 470.7L566.6 342.7z"/>
            </svg>
            Log out
          </button>}
        </div>
      )}
    </div>
        </div>

        {/* Chat container */}
        
        <div className={isDarkMode ? "flex-auto p-2 sm:p-6 justify-between flex flex-col h-screen w-full md:w-10/12 bg-slate-900" : "flex-auto p-2 sm:p-6 border justify-between flex flex-col h-screen w-full md:w-10/12 bg-white"}>
          {/* Top bar with "open history" button on small screens */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              className={" px-1 py-1 rounded-full text-sm" +
    (isDarkMode ? "text-gray-100 hover:bg-slate-700" : "text-black hover:bg-gray-200")
  }
              onClick={() => setIsHistoryOpen(true)}
            >
              <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    className="w-5 h-5"
    fill="currentColor"
  >
    <path d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z"/>
  </svg>
            </button>
            <a href="/" className="mx-2 flex"> <img alt="Consilia logo" className="w-8" src="/logo512.png" />
            <h1 className={"text-lg font-semibold font-josefin-sans" + (isDarkMode ? "text-gray-100 " : "text-black")}>
              CONSILIA
            </h1>
            </a>

            <button
    type="button"
    onClick={() => setIsDarkMode((prev) => !prev)}
    className={
      "text-xs px-3 py-1 rounded-full border " +
      (isDarkMode
        ? "border-slate-500 bg-slate-800 hover:bg-slate-700"
        : "border-gray-300 bg-white hover:bg-gray-100")
    }
  >
    {isDarkMode ? "☀️ Light" : "🌙 Dark"}
  </button>
          </div>

          

          {/* Messages */}
          <div
            id="messages"
            className="flex flex-col space-y-4 p-3 overflow-y-auto scrollbar-thumb-blue scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-w-2 scrolling-touch"
            
          >
            {messages.map((message, index) => (
              <div key={index} id={`message-${index}`}>
                <div
                  className={
                    "flex items-end " +
                    (message.from === "bot" ? "" : "justify-end")
                  }
                >
                  <div
                    className={
                      "flex flex-col space-y-2 text-md leading-tight max-w-lg mx-4 " +
                      (message.from === "bot"
                        ? "order-2 items-start"
                        : "order-1 items-end")
                    }
                  >
                    <div>
                      <span
                        className={
                          "px-4 py-3 rounded-xl inline-block text-left " +
                          (message.from === "bot"
                            ? "rounded-bl-none bg-gray-200 text-gray-800"
                            : "rounded-br-none bg-blue-500 text-white")
                        }
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ node, children, ...props }) => (
                              <h1
                                className="text-lg font-bold mb-1"
                                {...props}
                              >{children}</h1>
                            ),
                            h2: ({ node, children, ...props }) => (
                              <h2
                                className="text-md font-semibold mt-1 mb-1"
                                {...props}
                              >{children}</h2>
                            ),
                            h3: ({ node, children, ...props }) => (
                              <h3
                                className="text-md font-semibold mt-1 mb-1"
                                {...props}
                              >{children}</h3>
                            ),
                            p: ({ node, ...props }) => (
                              <p className="mb-1 leading-snug" {...props} />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul className="list-disc ml-5 mb-1" {...props} />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                className="list-decimal ml-5 mb-1"
                                {...props}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li className="mb-0.5" {...props} />
                            ),
                            a: ({ node, children, ...props }) => (
                              <a
                                className="underline text-blue-600"
                                target="_blank"
                                rel="noreferrer"
                                {...props}
                              >{children}</a>
                            ),
                            strong: ({ node, ...props }) => (
                              <strong
                                className="font-semibold"
                                {...props}
                              />
                            ),
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-2 text-md leading-tight mx-2 order-2 items-start w-48  ">
                  </div>
              </div>
            ))}

            {/* Typing animation (controlled by botTyping) */}
            {botTyping && (
              <div className="flex flex-col space-y-2 text-md leading-tight mx-2 order-2 items-start w-48  ">
                  
                  <DotLottieReact
                  src="https://lottie.host/90e781d8-0bc8-4e22-9eea-29061dbcd100/2FVQeWhVUE.lottie"
                  loop
                  autoplay
                  />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          
          <div className="border-gray-200 flex place-content-center w-full px-4 pt-4 mb-2 sm:mb-0">
            <form className="w-full max-w-2xl" onSubmit={handleSubmit}>
              <div className="relative flex">
                <input
                  type="text"
                  placeholder="Tell me about your case"
                  autoComplete="off"
                  className="text-md w-full focus:outline-none focus:placeholder-gray-400 text-gray-600 placeholder-gray-600 pl-5 pr-16 bg-gray-100 transition duration-200 ease-in-out border-2 border-gray-200 hover:border-blue-200 focus:border-blue-500 rounded-full py-2"
                  value={inputValue}
                  maxLength={4000}
                  onChange={(e) => setInputValue(e.target.value)}
                />

                <div className="absolute right-2 items-center inset-y-0 flex">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-full h-8 w-8 transition duration-200 ease-in-out text-white bg-blue-500 hover:bg-blue-600 focus:outline-none"
                  >
                    ↑
                  </button>
                </div>
              </div>
              <p className="text-center text-xs font-light">Consilia can make mistakes. Check important info.</p>
            </form>
          </div>
          
        </div>
        
      </div>
    </>
  );
}

export default Home;
